import { useQueryClient } from 'react-query'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { ThumbDown, ThumbUp } from 'blaze-ui/icons'
import { useCreateFeedback } from 'hooks/queries/useCreateFeedback'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { filesKeys } from 'hooks/queries/files/filesKeys'
import { Text } from 'blaze-ui/Text/Text'
import Styles from './ContentRating.module.scss'
import { useAuth } from 'hooks/UseAuth'
import { useCallback, useState } from 'react'
import { useUpdateFeedback } from 'hooks/queries/feedbacks/useUpdateFeedback'
import { Feedback } from 'types'
import { PressEvent } from '@react-types/shared/src/events'
import { Upvote } from './Upvote'
import { Downvote } from './Downvote'
import cx from 'classnames'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

interface ContentRatingProps {
  subjectId: number
  fileKey: string
  initialRated?: boolean
  className?: string
}

type Rating = 'upvote' | 'downvote' | 'dismiss'

export const ContentRating = ({ subjectId, fileKey, initialRated, className }: ContentRatingProps) => {
  const [rating, setRating] = useState<Rating | null>(null)
  const [closeRating, setCloseRating] = useState(initialRated)
  const [showTellUsMore, setShowTellUsMore] = useState(false)
  const [localFeedback, setLocalFeedback] = useState<Feedback>()
  const [extraFeedback, setExtraFeedback] = useState('')
  const auth = useAuth()
  const queryClient = useQueryClient()
  const workspaceId = useCurrentWorkspaceId()
  const { isMobile } = useResponsiveDesign()

  const { mutate: createFeedback, isLoading: isCreatingFeedback } = useCreateFeedback({
    onSuccess: (feedback) => {
      setLocalFeedback(feedback)
      setRating(feedback.rating as Rating)

      if (feedback.rating === 'upvote') {
        setTimeout(() => {
          setCloseRating(true)
        }, 5000)
      }
    },
  })

  const { mutate: updateFeedback } = useUpdateFeedback(localFeedback?.id ?? 0, {
    onSettled: (data) => {
      setLocalFeedback(data)
      if (localFeedback?.rating === 'upvote') {
        setShowTellUsMore(false)
        handleClose()
      }
    },
  })

  const handleClose = () => {
    setCloseRating(true)
    setShowTellUsMore(false)
    queryClient.invalidateQueries(filesKeys.detail(workspaceId, fileKey))
  }

  const handleRate = (rating: Rating) => {
    createFeedback({ rating, subject: { type: 'AISuggestionRequest', id: subjectId } })
  }

  const handleUpvote = () => {
    handleRate('upvote')
  }

  const handleDownvote = () => {
    handleRate('downvote')
  }

  const handleDismiss = () => {
    handleRate('dismiss')
    handleClose()
  }

  const handleChipPress = useCallback(
    (e: PressEvent) => {
      const feedback = e.target.getAttribute('data-key')

      if (!feedback || !localFeedback) return

      updateFeedback({
        feedback: {
          ...localFeedback.feedback,
          [feedback]: !localFeedback.feedback?.[feedback],
        },
      })
    },
    [updateFeedback, localFeedback],
  )

  if (!auth || isMobile || (closeRating && !showTellUsMore)) return null

  if (rating === 'upvote') {
    return (
      <Upvote
        showTellUsMore={showTellUsMore}
        setShowTellUsMore={setShowTellUsMore}
        extraFeedback={extraFeedback}
        setExtraFeedback={setExtraFeedback}
        onSubmit={() => updateFeedback({ feedback: { rating: 'upvote', feedback: extraFeedback } })}
        onClose={handleClose}
        className={className}
      />
    )
  }

  if (rating === 'downvote') {
    return (
      <Downvote
        showTellUsMore={showTellUsMore}
        setShowTellUsMore={setShowTellUsMore}
        extraFeedback={extraFeedback}
        setExtraFeedback={setExtraFeedback}
        onChipPress={handleChipPress}
        localFeedback={localFeedback}
        onClose={handleClose}
        className={className}
        onSubmit={() => {
          updateFeedback({ feedback: { rating: 'downvote', feedback: extraFeedback, ...localFeedback?.feedback } })
          handleClose()
        }}
      />
    )
  }

  return (
    <div className={cx(Styles.root, className)}>
      <Text variant="secondary">Do you like the result?</Text>
      <div className={Styles.buttons}>
        <IconButton
          aria-label="Thumbs down"
          onPress={handleDownvote}
          icon={() => <ThumbDown size={16} />}
          variant="secondary"
          size="md"
          isDisabled={isCreatingFeedback}
        />
        <IconButton
          aria-label="Thumbs up"
          onPress={handleUpvote}
          icon={() => <ThumbUp size={16} />}
          variant="secondary"
          size="md"
          isDisabled={isCreatingFeedback}
        />
        <IconButton
          aria-label="Dismiss"
          onPress={handleDismiss}
          variant="secondary"
          size="md"
          isDisabled={isCreatingFeedback}
        >
          Close
        </IconButton>
      </div>
    </div>
  )
}
